import { computed, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Contact } from "@models/common/contact";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { IndividualData } from "@models/individuals/individual-data";
import { IndividualDto } from "@models/individuals/individual-dto";
import { IndividualVerificationModel } from "@models/individuals/individual-verification.model";
import { RelationshipIndividualDto } from "@models/individuals/relationship-individual-dto";
import { ConnectionStatusEnum } from "@models/integrations/connection-status.enum";
import { Relationship } from "@models/relationships/relationship";
import { OrganisationSettingData } from "@models/settings/organisation-setting-data";
import { MatchesSelected } from "@models/ui-models/bulk-change-data";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { patchState, signalState } from "@ngrx/signals";
import {
  ChangeRelationshipContactDetailsModalComponent
} from "@pages/individuals/components/individual-profile-page/components/change-relationship-contact-details-modal/change-relationship-contact-details-modal.component";
import {
  EditIndividualDinModalComponent
} from "@pages/individuals/components/individual-profile-page/components/edit-individual-din-modal/edit-individual-din-modal.component";
import {
  EditStringModalComponent
} from "@pages/individuals/components/individual-profile-page/components/edit-string-modal/edit-string-modal.component";
import {
  SelectIndividualsModalComponent
} from "@pages/individuals/components/individual-profile-page/components/select-individuals-modal/select-individuals-modal.component";
import {
  UpdateIndividualPropsModalComponent
} from "@pages/individuals/components/individual-profile-page/components/update-individual-props-modal/update-individual-props-modal.component";
import {
  UIIndividualWarningStates
} from "@pages/individuals/components/individual-profile-page/constants/ui-individual-warning-states";
import {
  IndividualRelationshipGroupModel
} from "@pages/individuals/components/individual-profile-page/models/individual-relationship-group-model";
import {
  getUiIndividualWarnings
} from "@pages/individuals/components/individual-profile-page/utils/get-ui-individual-warning-states";
import {
  IContactsChangeEvent
} from "@pages/individuals/components/individuals-page/components/contacts/contacts.component";
import { IndividualProfileGridService } from "@pages/individuals/services/individual-profile-grid.service";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  ProviderConnectionStatus
} from "@shared/components/documents-related/sign-integrations-dropdown/model/provider-connection-status";
import { addressQueryParamKey, relationshipQueryParamKey } from "@shared/constants/query-param-keys.constant";
import { CountryPipe } from "@shared/pipes/countryPipe";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { ContactsService } from "@shared/services/contacts.service";
import { ESignApiService } from "@shared/services/e-sign-api.service";
import { EntityIdentityApiService } from "@shared/services/entity-identity-api.service";
import { IndividualsApiService } from "@shared/services/individuals-api.service";
import { NotesApiService } from "@shared/services/notes-api.service";
import { OrganisationSettingsService } from "@shared/services/organisation-settings.service";
import { doNothing } from "@shared/utils/doNothing";
import { getQuantityLabel } from "@shared/utils/get-quantity-label";
import { Guid } from "@shared/utils/guid.helper";
import { ToastrService } from "ngx-toastr";
import { Observable, of, tap } from "rxjs";
import {
  BulkCompanyChangeAddressComponent,
  BulkCompanyChangePartyNameComponent
} from "../../../forms/asic-forms/bulks";
import {
  CompanyChangeAddressComponent,
  CompanyChangePartyNameComponent,
  CompanyMistakeCorrectionComponent
} from "../../../forms/asic-forms/company";
import { CompanyChangeAddress, CompanyChangePartyName, CompanyMistakeCorrection } from "../../../forms/models/company";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

export type IndividualProfileLoadingKey = 'isIndividualLoading'
  | 'isIsXpmConnectedLoading'
  | 'isContactsLoading'
  | 'isSplitLoading'
  | 'isCompanyLoading';

export interface IIndividualRelationshipsViewModel {
  relationships: (IndividualRelationshipGroupModel | string)[];
  isShowPastOfficersToggleVisible: boolean;
  isPastOfficersVisible: boolean;
  entityRelationshipsCount: Record<string, number>;
}

export interface IIndividualContactsState {
  isVisible: boolean;
}

export interface IIndividualContactsViewModel extends IIndividualContactsState {
  total: number;
  label: string;
}

export interface IIndividualNotesState {
  isVisible: boolean;
  total: number;
}

export interface IIndividualNotesViewModel extends IIndividualNotesState {
  label: string;
}

export interface IIndividualAlertsState {
  isVisible: boolean;
}

export interface IIndividualAlertsViewModel extends IIndividualAlertsState {
  label: string;
  total: number;
}

export interface IIndividualProfileState {
  loaders: Record<IndividualProfileLoadingKey, boolean>;

  individualId: string;
  individualDto: IndividualDto;
  individualVerificationModel: IndividualVerificationModel | null;

  isPastOfficersVisible: boolean;

  contacts: IIndividualContactsState,
  notes: IIndividualNotesState,
  alerts: IIndividualAlertsState,

  organisationSettings: OrganisationSettingData | null;
  currentProviderConnectionStatus: ProviderConnectionStatus | null;
  isXpmConnected: boolean;
}

const individualProfileInitialState: IIndividualProfileState = {
  loaders: {
    isIndividualLoading: false,
    isIsXpmConnectedLoading: false,
    isContactsLoading: false,
    isSplitLoading: false,
    isCompanyLoading: false,
  },

  individualId: '',
  individualDto: new IndividualDto(),
  individualVerificationModel: null,

  isPastOfficersVisible: false,

  contacts: {
    isVisible: false,
  },
  notes: {
    total: 0,
    isVisible: false,
  },
  alerts: {
    isVisible: false,
  },

  organisationSettings: null,
  currentProviderConnectionStatus: null,
  isXpmConnected: false,
};

@Injectable()
export class IndividualProfileStateService {
  private individualApiService = inject(IndividualsApiService);
  private entityIdentityApiService = inject(EntityIdentityApiService);
  private eSignApiService = inject(ESignApiService);
  private notesApiService = inject(NotesApiService);
  private organisationSettingsService = inject(OrganisationSettingsService);
  private contactsApiService = inject(ContactsApiService);
  private companiesApiService = inject(CompaniesApiService);

  private individualProfileGridService = inject(IndividualProfileGridService);
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private contactsService = inject(ContactsService);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  state = signalState<IIndividualProfileState>(individualProfileInitialState);

  // =====  Selectors  =====

  isModalOpened = this.modalFormsService.modalOpened;
  isControlsDisabled = computed(() => this.isModalOpened() || Object.values(this.state.loaders()).some((isLoading) => isLoading));
  placeOfBirth = computed<string>(() => CountryPipe.formatPlaceOfBirth(this.state.individualDto().birthCity, this.state.individualDto().birthCountry));
  warningStates = computed(() => getUiIndividualWarnings(this.state.individualDto()));

  individualRelationshipsViewModel = computed<IIndividualRelationshipsViewModel>(() => {
    const relationships: (IndividualRelationshipGroupModel | string)[] = [];
    const individualDto = this.state.individualDto();
    const isPastOfficersVisible = this.state.isPastOfficersVisible();
    const companyRelationshipsGroups = individualDto.companyRelationshipsGroups;
    const companyActiveRelationshipsGroups = individualDto.companyActiveRelationshipsGroups;

    const individualTrustRelationshipsGroups = individualDto.trustRelationshipGroups;
    const individualCompanyRelationshipsGroups = isPastOfficersVisible ? companyRelationshipsGroups : companyActiveRelationshipsGroups;
    const isShowPastOfficersToggleVisible = companyRelationshipsGroups.length != companyActiveRelationshipsGroups.length;

    if (individualCompanyRelationshipsGroups.length) {
      relationships.push('Companies');
      relationships.push(...individualCompanyRelationshipsGroups);
    }

    if (individualTrustRelationshipsGroups.length) {
      relationships.push('Trusts');
      relationships.push(...individualTrustRelationshipsGroups);
    }

    const entityRelationshipsCount = {};

    individualDto.relationshipIndividualDtoList
      .filter((r) => !r.isResigned)
      .forEach((relationship) => {
        if (entityRelationshipsCount[relationship.entityId]) {
          entityRelationshipsCount[relationship.entityId]++;
        } else {
          entityRelationshipsCount[relationship.entityId] = 1;
        }
      });

    return {
      relationships,
      isShowPastOfficersToggleVisible,
      isPastOfficersVisible,
      entityRelationshipsCount
    };
  });

  contactsViewModel = computed<IIndividualContactsViewModel>(() => {
    const contacts = this.state.individualDto.contacts();
    const isVisible = this.state.contacts.isVisible();
    const total = contacts.length;
    const label = getQuantityLabel(total, 'Contact');

    return { isVisible, total, label };
  });

  notesViewModel = computed<IIndividualNotesViewModel>(() => {
    const notesState = this.state.notes();
    const label = getQuantityLabel(this.state.notes().total);

    return { ...notesState, label };
  });

  alertsViewModel = computed<IIndividualAlertsViewModel>(() => {
    const alertsState = this.state.alerts();
    const total = this.state.individualDto.alerts().length;
    const label = getQuantityLabel(total, 'Alert');

    return { ...alertsState, total, label };
  });

  // =====  State Mutations  =====

  setIsLoading(key: IndividualProfileLoadingKey, isLoading = true): void {
    patchState(this.state, (state) => ({ loaders: { ...state.loaders, [key]: isLoading } }));
  }

  setIsPastOfficersVisible(isPastOfficersVisible: boolean): void {
    patchState(this.state, () => ({ isPastOfficersVisible }));
  }

  patchIndividualContacts(event: IContactsChangeEvent): void {
    patchState(this.state, (state) => ({
      individualDto: new IndividualDto({
        ...state.individualDto,
        contacts: event.contacts,
        primaryContact: event.primaryContact ?? null,
        primaryContactId: event.primaryContact?.contactId ?? null
      }),
    }));
  }

  patchContactsState(contactsState: Partial<IIndividualContactsState>): void {
    patchState(this.state, (state) => ({ contacts: { ...state.contacts, ...contactsState } }));
  }

  patchNotesState(notesState: Partial<IIndividualNotesState>): void {
    patchState(this.state, (state) => ({ notes: { ...state.notes, ...notesState } }));
  }

  patchAlertsState(alertsState: Partial<IIndividualAlertsState>): void {
    patchState(this.state, (state) => ({ alerts: { ...state.alerts, ...alertsState } }));
  }

  // =====  Life Cycle  =====

  init(individualId: string): void {
    patchState(this.state, () => ({ individualId }));

    this.loadIndividualDto();
    this.loadIsXpmConnected();
    this.loadESignProvidersStatus();
    this.loadOrganisationSettings();
    this.loadNotesTotal();
  }

  // =====  Request Actions  =====

  loadIndividualDto(): void {
    this.setIsLoading('isIndividualLoading');

    this.getIndividualDto()
      .subscribe({
        next: (individualDto) => {
          patchState(this.state, () => ({ individualDto }));
          this.setIsLoading('isIndividualLoading', false);
        },
        error: (error) => {
          console.warn('[loadIndividualDto] error: ', error);
          this.toastr.error('Failed to load individual profile', 'Error');
          void this.router.navigate(['/individuals']);
        }
      });
  }

  loadIsXpmConnected(): void {
    this.setIsLoading('isIsXpmConnectedLoading');

    this.getIsXpmConnected()
      .subscribe({
        next: (isXpmConnected) => {
          patchState(this.state, () => ({ isXpmConnected }));
          this.setIsLoading('isIsXpmConnectedLoading', false);
        },
        error: (error) => {
          console.warn('[loadIsXpmConnected] error: ', error);
          this.toastr.error('Failed to load XPM connection status', 'Error');
        }
      });
  }

  loadESignProvidersStatus(): void {
    this.getESignProvidersStatus()
      .subscribe({
        next: (providerConnectionStatuses) => {
          const currentProviderConnectionStatus = providerConnectionStatuses.find((providerConnectionStatus) => providerConnectionStatus.status == ConnectionStatusEnum.Connected) ?? null;
          patchState(this.state, () => ({ currentProviderConnectionStatus }));
        },
        error: (error) => {
          console.warn('[loadESignProvidersStatus] error: ', error);
          this.toastr.error('Failed load eSign providers', 'Error');
          patchState(this.state, () => ({ currentProviderConnectionStatus: null }));
        }
      });
  }

  loadOrganisationSettings(): void {
    this.getOrganisationSettings()
      .subscribe({
        next: (organisationSettings) => {
          patchState(this.state, () => ({ organisationSettings }));
        },
        error: (error) => {
          console.warn('[loadESignProvidersStatus] error: ', error);
          this.toastr.error('Failed load eSign providers', 'Error');
          patchState(this.state, () => ({ organisationSettings: null }));
        }
      });
  }

  loadNotesTotal(): void {
    this.getNotesTotal()
      .subscribe({
        next: (total) => this.patchNotesState({ total }),
        error: (error) => {
          console.warn('[loadNotesTotal] error: ', error);
          this.toastr.error('Failed to load total notes quantity', 'Error');
        }
      });
  }

  loadContacts(): void {
    this.setIsLoading('isContactsLoading');

    this.getContacts()
      .subscribe({
        next: (contacts) => {
          patchState(this.state, (state) => {
            const individualDto = new IndividualDto({ ...state.individualDto, contacts });

            return { individualDto };
          });
          this.setIsLoading('isContactsLoading', false);
        },
        error: (error) => {
          console.warn('[loadContacts] error: ', error);
          this.toastr.error('Failed to load individual contacts', 'Error');
        }
      });
  }

  loadIndividualVerification(): void {
    //untill webhooks will be implemented
    this.getVerification()
      .subscribe({
        next: (individualVerificationModel) => {
          patchState(this.state, () => ({ individualVerificationModel }));
        },
        error: (error) => {
          console.warn('[loadIndividualVerification] error: ', error);
          this.toastr.error('Failed load individual verification', 'Error');
          patchState(this.state, () => ({ individualVerificationModel: null }));
        }
      });
  }

  // =====  Modals  =====

  openUpdateAddressModal(): void {
    const addressToUpdate = this.warningStates().individualPropsToUpdate[UIIndividualWarningStates.Address];
    if (!addressToUpdate) return;

    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(UpdateIndividualPropsModalComponent, { size: "lg" });
    const instance = modalRef.componentInstance as UpdateIndividualPropsModalComponent;

    instance.header = 'Update Address';
    instance.confirm = this.individualApiService.updateAddress(individualDto.individualId, addressToUpdate);
    instance.responseError = 'Failed to update address';
    instance.changeType = UIIndividualWarningStates.Address;
    instance.valueBefore = individualDto.address.normalizedFullAddress;
    instance.valueAfter = addressToUpdate.normalizedFullAddress;

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openUpdateDateOfBirthModal(): void {
    const dobToUpdate = this.warningStates().individualPropsToUpdate[UIIndividualWarningStates.Dob];
    if (!dobToUpdate) return;

    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(UpdateIndividualPropsModalComponent, { size: "sm" });
    const instance = modalRef.componentInstance as UpdateIndividualPropsModalComponent;

    instance.header = 'Update Date of Birth';
    instance.confirm = this.individualApiService.updateIndividualPart('dob', individualDto.individualId, dobToUpdate);
    instance.responseError = 'Failed to update Date of Birth';
    instance.changeType = UIIndividualWarningStates.Dob;
    instance.valueBefore = individualDto.dob?.toString() ?? '';
    instance.valueAfter = dobToUpdate;

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openUpdateBirthPlaceModal(): void {
    this.toastr.error('Update Place of Birth is not available now', 'Error');
  }

  openEditDinModal(): void {
    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(EditIndividualDinModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as EditIndividualDinModalComponent;

    instance.individualId = individualDto.individualId;
    instance.din = individualDto.din;

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openEditFamilyGroupModal(): void {
    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(EditStringModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as EditStringModalComponent;

    instance.config = {
      modalHeader: 'Update Family Group',
      modalIcon: 'ic-refresh-20',
      controlInitialValue: individualDto.familyGroup,
      controlLabel: 'Family Group',
      confirm: (familyGroup) => this.individualApiService.updateIndividualPart('family-group', individualDto.individualId, familyGroup),
      validators: [],
      isControlValid: () => true,
      responseError: 'Failed to update Family Group',
    };

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openEditReferenceNumberModal(): void {
    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(EditStringModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as EditStringModalComponent;

    instance.config = {
      modalHeader: 'Update Reference Number',
      modalIcon: 'ic-refresh-20',
      controlInitialValue: individualDto.referenceNumber,
      controlLabel: 'Reference Number',
      confirm: (referenceNumber) => this.individualApiService.updateIndividualPart('reference-number', individualDto.individualId, referenceNumber),
      validators: [],
      isControlValid: () => true,
      responseError: 'Failed to update Reference Number',
    };

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openAssignIndividualToExistingIndividualModal(relationships: RelationshipIndividualDto[]): void {
    if (!this.isSplitValid(relationships.length)) return;

    const individualDto = this.state.individualDto();
    const relationshipsIds = relationships.map((relationship) => relationship.relationshipId);

    const modalRef = this.modalService.open(SelectIndividualsModalComponent);
    const instance = modalRef.componentInstance as SelectIndividualsModalComponent;

    instance.individual = individualDto;
    instance.relationshipsIds = relationshipsIds!;

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  splitSelectedRelationshipToNewIndividual(relationships: RelationshipIndividualDto[]): void {
    if (!this.isSplitValid(relationships.length)) return;

    const relationshipsIds = relationships.map((relationship) => relationship.relationshipId);
    this.setIsLoading('isSplitLoading');

    this.executeSplitRelationshipToNewIndividual(relationshipsIds)
      .subscribe({
        next: () => {
          this.setIsLoading('isSplitLoading', false);
          this.loadIndividualDto();
        },
        error: (error) => {
          console.warn('[IndividualComponent.newAssignToExisting] error: ', error);
          this.toastr.error('Error while splitting individuals', 'Error');
        }
      });
  }

  openAddContactModal(): void {
    const individualDto = this.state.individualDto();
    const { instance, result } = this.contactsService.openEditContactModal();

    instance.individual = individualDto;

    result.then(() => this.loadContacts(), doNothing);
  }

  openUpdateRelationshipContactDetails(relationships: RelationshipIndividualDto[]): void {
    if (!relationships.length) return;

    if (this.isPastOfficersPresentInRelationships(relationships)) {
      return;
    }

    const individualDto = this.state.individualDto();

    const modalRef = this.modalService.open(ChangeRelationshipContactDetailsModalComponent, { size: 'lg' });
    const instance = modalRef.componentInstance as ChangeRelationshipContactDetailsModalComponent;

    instance.individual = individualDto;
    instance.relationshipsToUpdate = relationships;

    instance.contactsChangedEvent.subscribe((event: IContactsChangeEvent) => this.patchIndividualContacts(event));

    modalRef.result.then(() => this.loadIndividualDto(), doNothing);
  }

  openSelectBulkChangeAddressMethodModal(relationships: RelationshipIndividualDto[]): void {
    if (this.isPastOfficersPresentInRelationships(relationships)) {
      return;
    }

    const modalRef = this.modalService.open(CommonActionsModalComponent, { size: 'sm' });
    const instance = modalRef.componentInstance as CommonActionsModalComponent;
    const instanceDestroyRef = instance.getDestroyRef();

    const actions: IActionButtonConfig[] = [
      {
        text: 'Yes',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            tap(() => this.redirectToBulkChangeAddressOnBulkChanges(relationships))
          )
      },
      {
        text: 'No',
        btnColor: 'white',
        action: of(null)
          .pipe(
            takeUntilDestroyed(instanceDestroyRef),
            tap(() => this.openBulkChangeAddressModalForSelectedAddresses(relationships))
          )
      },
    ];

    const body = `
      <div class="text-center">This address is linked to multiple entities.</div>
      <div class="text-center">Would you like to view all associated entities?</div>
    `;

    instance.config = {
      header: 'Change Addresses',
      iconClass: 'ic-change-address-20',
      showCloseBtn: false,
      body,
      actions,
    };
  }

  // =====  Forms  =====

  openCompanyChangePartyNameModal(relationship: RelationshipIndividualDto): void {
    if (relationship.relatedToEntityType != EntityType.Company) {
      this.toastr.warning('Name change is only available only for individuals in companies', 'Warning');
      return;
    }

    if (!relationship.entityId) {
      return;
    }

    const individualDto = this.state.individualDto();
    const entityRelationships = this.getRelationshipByEntityId(relationship.entityId);

    const company = new Company({ entityId: relationship.entityId });
    const payload = new CompanyChangePartyName({
      ...individualDto,
      relationshipIds: entityRelationships.map((relationship) => relationship.relationshipId),
    });

    company.officers = entityRelationships.map((relationship) => {
      return new Relationship({
        individualDataOverride: new IndividualData(relationship.individualDataOverride ?? {}),
        relationshipId: relationship.relationshipId,
        individualId: relationship.individualId,
        type: relationship.type
      });
    });

    const modalRef = this.modalFormsService.openModalWithCompany(payload, company);
    const instance = modalRef.componentInstance as CompanyChangePartyNameComponent;

    instance.redirectAfterSubmit = true;
  }

  openCompanyChangeAddressForm(groupModel: IndividualRelationshipGroupModel): void {
    const relationships = groupModel.relationshipsDtos;

    if (relationships.some((relationship) => relationship.relatedToEntityType != EntityType.Company)) {
      this.toastr.warning('Address change is only available only for individuals in companies', 'Warning');
      return;
    }

    const companyId = relationships[0].entityId;
    this.setIsLoading('isCompanyLoading');

    this.getCompany(companyId)
      .subscribe({
        next: (company) => {
          const entityRelationships = this.getRelationshipByEntityId(relationships[0].entityId);
          const payload = new CompanyChangeAddress({ relationshipIds: entityRelationships.map((relationship) => relationship.relationshipId) });

          const modalRef = this.modalFormsService.openModalWithCompany(payload, company);
          const instance = modalRef.componentInstance as CompanyChangeAddressComponent;

          instance.redirectAfterSubmit = true;

          void modalRef.result.finally(() => this.deselectRelationships());
          this.setIsLoading('isCompanyLoading', false);
        }
      });
  }

  openCompanyMistakeCorrectionModal(relationship: RelationshipIndividualDto): void {
    if (relationship.relatedToEntityType != EntityType.Company) {
      this.toastr.warning('Address change is only available only for individuals in companies', 'Warning');
      return;
    }

    const company = new Company({ entityId: relationship.entityId });
    const payload = new CompanyMistakeCorrection();

    const modalRef = this.modalFormsService.openModalWithCompany(payload, company);
    const instance = modalRef.componentInstance as CompanyMistakeCorrectionComponent;

    instance.redirectAfterSubmit = true;
  }

  // =====  Bulk Forms  =====

  openCompanyChangePartyNameModalBulk(relationships: RelationshipIndividualDto[]): void {
    if (this.isModalOpened() && relationships.length) return;

    if (this.isPastOfficersPresentInRelationships(relationships)) {
      return;
    }

    if (!this.areAllEntitiesRelationshipsSelected(relationships)) {
      return;
    }

    const relationshipIds = relationships.map((relationship) => relationship.relationshipId);
    const change = new CompanyChangePartyName({ relationshipIds, ...relationships[0].individualDataOverride });

    const modalRef = this.modalFormsService.openModalWithBulkCompany(change, new Company(), true);
    const instance = modalRef.componentInstance as BulkCompanyChangePartyNameComponent;

    instance.companiesIds = [...new Set(relationships.map((relationship) => relationship.entityId))];
    instance.trustOnly = relationships.every(r => r.relatedToEntityType == EntityType.Trust);
  }

  openBulkChangeAddressModalForSelectedAddresses(relationships: RelationshipIndividualDto[]): void {
    const entities = new Map<string, MatchesSelected>();

    relationships.forEach((relationship) => {
      if (entities.has(relationship.entityId)) {
        const matchSelected = entities.get(relationship.entityId);

        matchSelected?.relationshipIds.push(relationship.relationshipId);

      } else {
        const matchSelected: MatchesSelected = {
          entityId: relationship.entityId ?? Guid.EmptyGuid,
          name: relationship.entityName ?? '',
          acn: relationship.relatedToEntityType == EntityType.Company ? relationship.entityNumber : '',
          abn: relationship.relatedToEntityType == EntityType.Trust ? relationship.entityNumber : '',
          relationshipIds: [relationship.relationshipId],
        };

        entities.set(matchSelected.entityId, matchSelected);
      }
    });

    const bulkMatchesSelected = new Array(...entities.values());
    const change = new CompanyChangeAddress();

    const modalRef = this.modalFormsService.openModalWithBulkCompany(change, new Company());
    const instance = modalRef.componentInstance as BulkCompanyChangeAddressComponent;

    instance.bulkMatchesSelected = bulkMatchesSelected;
    instance.isIndividual = true;
    instance.trustOnly = relationships.every(r => r.relatedToEntityType == EntityType.Trust);
  }

  // =====  Requests  =====

  getIndividualDto(individualId = this.state.individualId()): Observable<IndividualDto> {
    return this.individualApiService.get(individualId);
  }

  getIsXpmConnected(): Observable<boolean> {
    return this.entityIdentityApiService.isXpmConnected();
  }

  getESignProvidersStatus(): Observable<ProviderConnectionStatus[]> {
    return this.eSignApiService.getESignProvidersStatus();
  }

  getOrganisationSettings(): Observable<OrganisationSettingData> {
    return this.organisationSettingsService.getOrganisationSettings();
  }

  getVerification(individualId = this.state.individualId()): Observable<IndividualVerificationModel> {
    return this.individualApiService.getVerification(individualId);
  }

  getNotesTotal(individualId = this.state.individualId()): Observable<number> {
    return this.notesApiService.getNotesTotal(individualId);
  }

  getContacts(individualId = this.state.individualId()): Observable<Contact[]> {
    return this.contactsApiService.getIndividualContacts(individualId);
  }

  executeSplitRelationshipToNewIndividual(relationshipsIds: string[]): Observable<void> {
    const individualId = this.state.individualId();
    return this.individualApiService.split(individualId, relationshipsIds, undefined);
  }

  getCompany(companyId: string): Observable<Company> {
    return this.companiesApiService.getById(companyId);
  }

  // =====  Utils  =====

  isSplitValid(relationshipsLength: number): boolean {
    if (relationshipsLength == 0) {
      this.toastr.warning("Please select relationship to split");
      return false;
    }

    if (relationshipsLength == this.state.individualDto().relationshipIndividualDtoList.length) {
      this.toastr.warning("You can't split all relationships of individual");
      return false;
    }

    return true;
  }

  isPastOfficersPresentInRelationships(relationships: RelationshipIndividualDto[]): boolean {
    if (relationships.some((relationship) => relationship.isResigned)) {
      this.toastr.error('You can not change past officers', 'Warning');
      return true;
    }

    return false;
  }

  areAllEntitiesRelationshipsSelected(checkedIndividualRelationships: RelationshipIndividualDto[]): boolean {
    if (!checkedIndividualRelationships.length) return false;

    const selectedEntitiesRelationshipsCount: Record<string, number> = {};

    checkedIndividualRelationships.forEach((relationship) => {
      if (selectedEntitiesRelationshipsCount[relationship.entityId]) {
        selectedEntitiesRelationshipsCount[relationship.entityId]++;
      } else {
        selectedEntitiesRelationshipsCount[relationship.entityId] = 1;
      }
    });

    const areAllSelected = Object.entries(selectedEntitiesRelationshipsCount)
      .every(([entityId, relationshipsCount]) => this.individualRelationshipsViewModel().entityRelationshipsCount[entityId] == relationshipsCount);

    if (!areAllSelected) {
      this.toastr.warning('This individual has an additional role within the same entity. Please select all roles associated with this entity to proceed.', 'Error');
    }

    return areAllSelected;
  }

  redirectToBulkChangeAddressOnBulkChanges(relationships: RelationshipIndividualDto[]): void {
    const addresses = relationships
      .filter((relationship) => relationship.individualDataOverride?.address)
      .map((relationship) => relationship.individualDataOverride!.address.normalizedFullAddress)
      .reduce((collection, relationshipAddress) => collection.add(relationshipAddress), new Set<string>());

    const queryParams = {
      [addressQueryParamKey]: [...addresses],
      [relationshipQueryParamKey]: relationships.map((relationship) => relationship.relationshipId)
    };

    void this.router.navigate(['/bulk-changes'], { queryParams });
  }

  getRelationshipByEntityId(entityId: string): RelationshipIndividualDto[] {
    return this.state.individualDto().relationshipIndividualDtoList.filter((relationship) => relationship.entityId == entityId);
  }

  deselectRelationships(): void {
    this.individualProfileGridService.gridApi.deselectAll();
  }
}
